import React, { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, MessageCircle, Search as SearchIcon } from 'lucide-react';
import { getInboxConversations } from '../services/api/implementation/impl';
import WhatsAppMessageThread from '../components/whatsapp/WhatsAppMessageThread';
import { INBOX_SUMMARY_KEY } from '../hooks/useInboxUnread';

const PAGE_LIMIT = 20;

function getInitials(name, phone) {
  const source = String(name || '').trim();
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits) return digits.slice(-2);
  return '?';
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMin = Math.floor((now - date) / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;

  const isSameDay =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isSameDay) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (isYesterday) return 'yesterday';

  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

const MEDIA_PREVIEW_LABELS = {
  document: '📄 Document',
  image: '🖼️ Photo',
  video: '🎥 Video',
  audio: '🎧 Audio'
};

// Pull the value of a JSON string field via regex, tolerating truncated JSON
// (including a value that is cut off before its closing quote).
function extractJsonField(text, field) {
  let match = text.match(new RegExp(`"${field}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`, 'i'));
  if (!match) {
    // Value truncated before its closing quote — capture to end of string.
    match = text.match(new RegExp(`"${field}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)$`, 'i'));
  }
  if (!match) {
    // Field present but value cut off mid-string (no closing quote yet).
    match = text.match(new RegExp(`"${field}"\\s*:\\s*"([^"]*)`, 'i'));
  }
  if (!match) return '';
  try {
    return JSON.parse(`"${match[1]}"`).trim();
  } catch {
    return match[1].trim();
  }
}

function humanizeName(name) {
  return String(name || '')
    .replace(/_/g, ' ')
    .replace(/\bv\d+\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function summarizePayload({ messageType, templateName, filename, caption, body, hasButtons }) {
  if (messageType === 'template' || templateName) {
    const name = humanizeName(templateName);
    return name ? `📄 ${name}` : '📄 Template';
  }
  if (MEDIA_PREVIEW_LABELS[messageType]) {
    const base = filename
      ? `${MEDIA_PREVIEW_LABELS[messageType].split(' ')[0]} ${filename}`
      : MEDIA_PREVIEW_LABELS[messageType];
    return caption ? `${base} · ${caption}` : base;
  }
  if (body) return body;
  if (caption) return caption;
  if (hasButtons) return '💬 Interactive message';
  return '';
}

// When preview text is a JSON payload we couldn't fully parse (often truncated
// before messageType/filename), never surface raw internal fields like userId.
function fallbackJsonPreview(text) {
  const summary = summarizePayload({
    messageType: extractJsonField(text, 'messageType') || extractJsonField(text, 'type'),
    templateName: extractJsonField(text, 'templateName'),
    filename: extractJsonField(text, 'filename') || extractJsonField(text, 'fileName'),
    caption: extractJsonField(text, 'messageText') || extractJsonField(text, 'caption'),
    body:
      extractJsonField(text, 'bodyText') ||
      extractJsonField(text, 'body') ||
      extractJsonField(text, 'text'),
    hasButtons: /"buttons"\s*:\s*\[/.test(text)
  });
  if (summary) return summary;

  // Outgoing API payloads lead with userId / vendorPhoneNumber — hide that noise.
  if (/"(userId|vendorPhoneNumber|recipientPhoneNumbers)"\s*:/.test(text)) {
    return '📄 Attachment';
  }
  if (/"(templateName|messageType|sourceUrl|filename)"\s*:/.test(text)) {
    return 'Message';
  }
  return 'Message';
}

// The API preview may contain a raw JSON payload for template / interactive /
// media messages, and it can be truncated. Turn it into a short readable summary.
function formatPreview(text) {
  if (typeof text !== 'string') return text || '';
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return text;

  // Fast path: valid JSON.
  try {
    const payload = JSON.parse(trimmed);
    if (payload && typeof payload === 'object') {
      const summary = summarizePayload({
        messageType: payload.messageType ?? payload.type,
        templateName: payload.templateName,
        filename: payload.filename ?? payload.fileName,
        caption: String(payload.messageText ?? payload.caption ?? '').trim(),
        body: String(payload.bodyText ?? payload.body ?? payload.text ?? '').trim(),
        hasButtons: Array.isArray(payload.buttons) && payload.buttons.length > 0
      });
      return summary || fallbackJsonPreview(trimmed);
    }
  } catch {
    // Truncated / invalid JSON — fall back to regex extraction below.
  }

  const summary = summarizePayload({
    messageType: extractJsonField(trimmed, 'messageType') || extractJsonField(trimmed, 'type'),
    templateName: extractJsonField(trimmed, 'templateName'),
    filename: extractJsonField(trimmed, 'filename') || extractJsonField(trimmed, 'fileName'),
    caption: extractJsonField(trimmed, 'messageText') || extractJsonField(trimmed, 'caption'),
    body:
      extractJsonField(trimmed, 'bodyText') ||
      extractJsonField(trimmed, 'body') ||
      extractJsonField(trimmed, 'text'),
    hasButtons: /"buttons"\s*:\s*\[/.test(trimmed)
  });
  return summary || fallbackJsonPreview(trimmed);
}

const ConversationSkeleton = () => (
  <div className="flex items-start gap-3 px-5 py-4">
    <div className="h-11 w-11 shrink-0 animate-pulse rounded-full bg-gray-200" />
    <div className="min-w-0 flex-1 space-y-2">
      <div className="h-3 w-1/2 animate-pulse rounded bg-gray-200" />
      <div className="h-3 w-3/4 animate-pulse rounded bg-gray-100" />
    </div>
  </div>
);

const WhatsAppInbox = () => {
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const sentinelRef = useRef(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['inboxConversations', { search }],
    queryFn: ({ pageParam = 1 }) =>
      getInboxConversations({
        page: pageParam,
        limit: PAGE_LIMIT,
        search: search || undefined
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.pagination;
      if (!pagination) return undefined;
      const loaded = pagination.page * pagination.limit;
      return loaded < pagination.total ? pagination.page + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    staleTime: 15000
  });

  const conversations = useMemo(
    () => (data?.pages || []).flatMap((page) => page?.conversations || []),
    [data]
  );

  const summary = data?.pages?.[0]?.summary || {
    unread_conversations: 0,
    unread_messages: 0
  };

  const selectedConversation = useMemo(
    () => conversations.find((item) => item.phone_number === selectedPhone) ?? null,
    [conversations, selectedPhone]
  );

  // Keep the nav/tab unread badge in sync with the live inbox summary.
  useEffect(() => {
    const liveSummary = data?.pages?.[0]?.summary;
    if (liveSummary) {
      queryClient.setQueryData(INBOX_SUMMARY_KEY, liveSummary);
    }
  }, [data, queryClient]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '150px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, conversations.length]);

  const headerSummary = (() => {
    if (isLoading) return 'Loading conversations…';
    if (summary.unread_conversations > 0) {
      const count = summary.unread_conversations;
      return `${count} unread conversation${count === 1 ? '' : 's'}`;
    }
    return 'WhatsApp conversations with your leads';
  })();

  return (
    <div className="px-6 py-6 pb-10">
      <div
        className="flex overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm"
        style={{ height: 'calc(100vh - var(--wa-campaign-shell-height, 8.25rem) - 4rem)' }}>
        <aside className="flex w-full max-w-sm min-h-0 flex-col border-r border-gray-100">
          <div className="border-b border-gray-100 px-5 py-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-bold text-gray-900">Inbox</h2>
              {summary.unread_messages > 0 ? (
                <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-white">
                  {summary.unread_messages > 99 ? '99+' : summary.unread_messages}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-gray-500">{headerSummary}</p>

            <div className="mt-3 flex items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-2">
              <SearchIcon className="h-4 w-4 shrink-0 text-gray-400" strokeWidth={2} />
              <input
                type="text"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search name or number"
                className="w-full bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex-1 divide-y divide-gray-100 overflow-hidden">
              {Array.from({ length: 6 }).map((_, index) => (
                <ConversationSkeleton key={index} />
              ))}
            </div>
          ) : isError ? (
            <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center text-gray-500">
              <AlertCircle className="mb-3 h-10 w-10 text-red-300" />
              <p className="text-sm font-medium text-gray-700">Couldn’t load conversations</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-3 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary/90">
                Try again
              </button>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center text-gray-500">
              <MessageCircle className="mb-3 h-10 w-10 text-gray-300" />
              <p className="text-sm font-medium text-gray-700">No conversations yet</p>
              <p className="mt-1 text-xs text-gray-500">
                {search
                  ? 'No conversations match your search.'
                  : 'Messages from leads will appear here once campaigns are running.'}
              </p>
            </div>
          ) : (
            <ul className="min-h-0 flex-1 divide-y divide-gray-100 overflow-auto">
              {conversations.map((item) => {
                const isActive = item.phone_number === selectedPhone;
                const displayName = item.contact_name || item.phone_number;
                return (
                  <li key={item.phone_number}>
                    <button
                      type="button"
                      onClick={() => setSelectedPhone(item.phone_number)}
                      className={`flex w-full items-start gap-3 px-5 py-4 text-left transition-colors ${
                        isActive ? 'bg-primary/5' : 'hover:bg-gray-50'
                      }`}>
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
                        {getInitials(item.contact_name, item.phone_number)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p
                            className={`truncate text-sm ${
                              item.unread_count > 0
                                ? 'font-bold text-gray-900'
                                : 'font-semibold text-gray-900'
                            }`}>
                            {displayName}
                          </p>
                          <span className="shrink-0 text-[11px] text-gray-400">
                            {formatRelativeTime(item.last_message_at)}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <p
                            className={`truncate text-xs ${
                              item.unread_count > 0 ? 'text-gray-700' : 'text-gray-500'
                            }`}>
                            {item.last_message_direction === 'outgoing' ? 'You: ' : ''}
                            {item.last_message_preview
                              ? formatPreview(item.last_message_preview)
                              : 'No messages yet'}
                          </p>
                          {item.unread_count > 0 ? (
                            <span className="flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-white">
                              {item.unread_count > 99 ? '99+' : item.unread_count}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}

              <li ref={sentinelRef} aria-hidden="true">
                {isFetchingNextPage ? (
                  <ConversationSkeleton />
                ) : !hasNextPage ? (
                  <p className="py-4 text-center text-[11px] text-gray-400">
                    No more conversations yet
                  </p>
                ) : null}
              </li>
            </ul>
          )}
        </aside>

        <section className="hidden min-h-0 min-w-0 flex-1 flex-col bg-[#F8F9FA] md:flex">
          {selectedConversation ? (
            <WhatsAppMessageThread
              key={selectedConversation.phone_number}
              conversation={selectedConversation}
              leadId={selectedConversation.lead_id ?? selectedConversation.leadId ?? null}
              projectId={selectedConversation.project_id ?? selectedConversation.projectId ?? null}
              onBack={() => setSelectedPhone(null)}
            />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center px-8 text-center text-gray-500">
              <MessageCircle className="mb-4 h-12 w-12 text-gray-300" />
              <p className="text-sm font-medium text-gray-700">Select a conversation</p>
              <p className="mt-1 max-w-sm text-xs text-gray-500">
                Choose a lead from the list to view messages.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default WhatsAppInbox;
